import { ListingData } from "@/server/interfaces/ListingData";
import React from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import Link from "next/link";
import SearchParamsHelper from "@/utils/SearchParamsHelper";
import Sorter, { SortItem } from "../inputs/Sorter";
import { useRouter } from "next/router";
import ListingItem from "./ListItem";
import SellerListingsCard from "../sellers/SellerListingsCard";

export const PaginatedListingsList = ({
  currentPage,
  listings,
  gridClass,
  queryParams,
  queryInfo,
  hasNext,
  hasPrevious,
  totalPages,
  onLoadMore,
  onEditAction,
  canLoadMore,
  pageTitle,
  pageDescription,
  canEdit,
  seller,
  children,
}: {
  seller: any;
  currentPage: any;
  listings: ListingData[];
  gridClass: string;
  queryParams: any;
  queryInfo: { pageTitle: string; pageDescription: string };
  hasNext: boolean;
  hasPrevious: boolean;
  totalPages: number;
  onLoadMore: () => void;
  onEditAction?: (action: string, Listing: ListingData) => void;
  canLoadMore: boolean;
  pageTitle?: string;
  pageDescription?: string;
  canEdit?: boolean;
  children?: React.ReactNode;
}) => {
  currentPage = currentPage ? parseInt(currentPage) : 1;
  const router = useRouter();

  const nextPageLink = hasNext
    ? SearchParamsHelper.getPageLink(currentPage + 1, queryParams)
    : null;
  const previousPageLink = hasPrevious
    ? SearchParamsHelper.getPageLink(currentPage - 1, queryParams)
    : null;

  const sortItems: SortItem[] = [
    {
      label: "Date: Newest to Oldest",
      value: "date_desc",
      selected: true,
    },
    {
      label: "Date: Oldest to Newest",
      value: "date_asc",
      selected: false,
    },
    {
      label: "Price: Low to High",
      value: "price_asc",
      selected: false,
    },
    {
      label: "Price: High to Low",
      value: "price_desc",
      selected: false,
    },
    {
      label: "Size: Low to High",
      value: "size_asc",
      selected: false,
    },
    {
      label: "Size: High to Low",
      value: "size_desc",
      selected: false,
    },
    {
      label: "Relevance",
      value: "relevance",
      selected: false,
    },
  ];

  const sort = (sortBy: string) => {
    let params = SearchParamsHelper.setSortParam(sortBy, queryParams);

    router.push("/Listings?" + params);
  };

  React.useEffect(() => {
    const listBottom = document.getElementById("list-bottom");
    if (listBottom) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            if (canLoadMore) {
              onLoadMore();
            }
          }
        },
        { rootMargin: "1000px", threshold: 1 }
      );

      observer.observe(listBottom);

      // Clean up function to unobserve when effect re-runs
      return () => observer.unobserve(listBottom);
    }
  }, [onLoadMore]);

  return (
    <>
      <div className="">
        {seller && (
          <div className="mb-5">
            <SellerListingsCard seller={seller} />
          </div>
        )}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            {pageTitle || queryInfo.pageTitle}
          </h1>
          {children && <div className="mb-5">{children}</div>}
        </div>
        <div className="sm:flex mb-5 justify-between items-center mt-2">
          <div>
            <p className="text-gray-600">
              {pageDescription || queryInfo.pageDescription}
            </p>
          </div>
          <div className="flex justify-end sm:mt-auto mt-3">
            <Sorter
              items={sortItems}
              onSort={(value) => {
                sort(value);
              }}
            />
          </div>
        </div>
      </div>
      {!listings.length && (
        <div>
          Sorry, we couldn't find any Listings matching your search criteria.
        </div>
      )}

      {listings.length > 0 && (
        <>
          <div className={gridClass}>
            {listings.map((Listing: ListingData, index: number) => (
              <div className="" key={index}>
                <ListingItem
                  Listing={Listing}
                  canEdit={canEdit}
                  onAction={(action: string, Listing: ListingData) => {
                    if (onEditAction) {
                      onEditAction(action, Listing);
                    }
                  }}
                />
              </div>
            ))}
          </div>

          <div className={"grid md:grid-cols-12 grid-cols-1 mt-20 mb-20"}>
            <div className="md:col-span-12 text-center">
              <nav>
                <ul className="inline-flex items-center space-x-8">
                  <li>
                    {hasPrevious && (
                      <a
                        href={"?" + previousPageLink}
                        className="w-10 h-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white  hover:text-white shadow-sm hover:border-green-600  hover:bg-green-600 "
                        title="Previous"
                      >
                        <MdKeyboardArrowLeft className="text-[20px]" />
                      </a>
                    )}
                    {!hasPrevious && (
                      <div className="w-10 h-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white  ">
                        <MdKeyboardArrowLeft className="text-[20px]" />
                      </div>
                    )}
                  </li>
                  <li>
                    Page {currentPage} of {totalPages}
                  </li>
                  <li>
                    {hasNext && (
                      <a
                        href={"?" + nextPageLink}
                        className="w-10 h-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white  hover:text-white shadow-sm hover:border-green-600  hover:bg-green-600 "
                        title="Next"
                      >
                        <MdKeyboardArrowRight className="text-[20px]" />
                      </a>
                    )}
                    {!hasNext && (
                      <div className="w-10 h-10 inline-flex justify-center items-center mx-1 rounded-full text-slate-400 bg-white">
                        <MdKeyboardArrowRight className="text-[20px]" />
                      </div>
                    )}
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </>
      )}
    </>
  );
};
